import { lazy, Suspense, type ReactNode } from "react";
import { Routes, Route, Navigate } from "react-router";
import { AppShell } from "@/components/app-shell";
import { useAuth } from "@/contexts/auth-context";

// Auth pages
const LoginPage = lazy(() =>
  import("@/pages/auth/login").then((module) => ({
    default: module.LoginPage,
  })),
);
const SignupPage = lazy(() =>
  import("@/pages/auth/signup").then((module) => ({
    default: module.SignupPage,
  })),
);

// App pages
const OverviewPage = lazy(() =>
  import("@/pages/overview/page").then((module) => ({
    default: module.OverviewPage,
  })),
);
const SetupPage = lazy(() =>
  import("@/pages/setup/index").then((module) => ({
    default: module.SetupPage,
  })),
);

// Research
const ResearchKeywordsPage = lazy(() =>
  import("@/pages/research/keywords").then((module) => ({
    default: module.ResearchKeywordsPage,
  })),
);
const ResearchPromptsPage = lazy(() =>
  import("@/pages/research/prompts").then((module) => ({
    default: module.ResearchPromptsPage,
  })),
);
const ResearchAppsPage = lazy(() =>
  import("@/pages/research/apps").then((module) => ({
    default: module.ResearchAppsPage,
  })),
);
const AppDetailPage = lazy(() =>
  import("@/pages/research/app-detail").then((module) => ({
    default: module.AppDetailPage,
  })),
);
const ResearchCompetitorsPage = lazy(() =>
  import("@/pages/research/competitors").then((module) => ({
    default: module.ResearchCompetitorsPage,
  })),
);
const ResearchLocalPage = lazy(() =>
  import("@/pages/research/local").then((module) => ({
    default: module.ResearchLocalPage,
  })),
);

// Tracking
const TrackingKeywordsPage = lazy(() =>
  import("@/pages/tracking/keywords").then((module) => ({
    default: module.TrackingKeywordsPage,
  })),
);
const TrackingAppsPage = lazy(() =>
  import("@/pages/tracking/apps").then((module) => ({
    default: module.TrackingAppsPage,
  })),
);
const TrackingCompetitorsPage = lazy(() =>
  import("@/pages/tracking/competitors").then((module) => ({
    default: module.TrackingCompetitorsPage,
  })),
);
const TrackingLocalPage = lazy(() =>
  import("@/pages/tracking/local").then((module) => ({
    default: module.TrackingLocalPage,
  })),
);
const TrackingGenaiPage = lazy(() =>
  import("@/pages/tracking/genai").then((module) => ({
    default: module.TrackingGenaiPage,
  })),
);

// Insights
const InsightsAlertsPage = lazy(() =>
  import("@/pages/insights/alerts").then((module) => ({
    default: module.InsightsAlertsPage,
  })),
);
const InsightsOpportunitiesPage = lazy(() =>
  import("@/pages/insights/opportunities").then((module) => ({
    default: module.InsightsOpportunitiesPage,
  })),
);
const InsightsAnomaliesPage = lazy(() =>
  import("@/pages/insights/anomalies").then((module) => ({
    default: module.InsightsAnomaliesPage,
  })),
);
const InsightsRecommendationsPage = lazy(() =>
  import("@/pages/insights/recommendations").then((module) => ({
    default: module.InsightsRecommendationsPage,
  })),
);

// Reports
const ReportsListPage = lazy(() =>
  import("@/pages/reports/list").then((module) => ({
    default: module.ReportsListPage,
  })),
);
const ReportDetailPage = lazy(() =>
  import("@/pages/reports/detail").then((module) => ({
    default: module.ReportDetailPage,
  })),
);

// Projects
const NewProjectPage = lazy(() =>
  import("@/pages/projects/new").then((module) => ({
    default: module.NewProjectPage,
  })),
);

// Assets
const AssetsListPage = lazy(() =>
  import("@/pages/assets/list").then((module) => ({
    default: module.AssetsListPage,
  })),
);
const AssetDetailPage = lazy(() =>
  import("@/pages/assets/detail").then((module) => ({
    default: module.AssetDetailPage,
  })),
);

// Chat
const ChatPage = lazy(() =>
  import("@/pages/chat/index").then((module) => ({ default: module.ChatPage })),
);
const ChatThreadPage = lazy(() =>
  import("@/pages/chat/thread").then((module) => ({
    default: module.ChatThreadPage,
  })),
);

// Settings
const SettingsWorkspacePage = lazy(() =>
  import("@/pages/settings/workspace").then((module) => ({
    default: module.SettingsWorkspacePage,
  })),
);
const SettingsAccountPage = lazy(() =>
  import("@/pages/settings/account").then((module) => ({
    default: module.SettingsAccountPage,
  })),
);
const SettingsIntegrationsPage = lazy(() =>
  import("@/pages/settings/integrations").then((module) => ({
    default: module.SettingsIntegrationsPage,
  })),
);

function RouteLoadingFallback() {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 240,
        color: "var(--color-text-muted)",
        fontFamily: "Inter, system-ui, sans-serif",
      }}
    >
      Loading page...
    </div>
  );
}

function withRouteSuspense(children: ReactNode) {
  return <Suspense fallback={<RouteLoadingFallback />}>{children}</Suspense>;
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { isLoggedIn, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          background: "var(--color-bg-primary)",
          color: "var(--color-text-muted)",
          fontFamily: "Inter, system-ui, sans-serif",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: 32,
              height: 32,
              border: "3px solid var(--color-border)",
              borderTopColor: "var(--color-accent)",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
              margin: "0 auto 12px",
            }}
          />
          <p style={{ fontSize: 14 }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const { isLoggedIn } = useAuth();

  if (isLoggedIn) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export function AppRouter() {
  return (
    <Routes>
      {/* Public auth routes */}
      <Route
        path="/login"
        element={
          <RedirectIfAuthed>
            {withRouteSuspense(<LoginPage />)}
          </RedirectIfAuthed>
        }
      />
      <Route
        path="/signup"
        element={
          <RedirectIfAuthed>
            {withRouteSuspense(<SignupPage />)}
          </RedirectIfAuthed>
        }
      />

      {/* Protected app routes */}
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        {/* Overview */}
        <Route path="/" element={withRouteSuspense(<OverviewPage />)} />

        {/* Onboarding Setup */}
        <Route path="/setup" element={withRouteSuspense(<SetupPage />)} />

        {/* Projects */}
        <Route
          path="/projects/new"
          element={withRouteSuspense(<NewProjectPage />)}
        />

        {/* Research */}
        <Route
          path="/research"
          element={<Navigate to="/research/keywords" replace />}
        />
        <Route
          path="/research/keywords"
          element={withRouteSuspense(<ResearchKeywordsPage />)}
        />
        <Route
          path="/research/prompts"
          element={withRouteSuspense(<ResearchPromptsPage />)}
        />
        <Route
          path="/research/apps"
          element={withRouteSuspense(<ResearchAppsPage />)}
        />
        <Route
          path="/research/apps/:appId"
          element={withRouteSuspense(<AppDetailPage />)}
        />
        <Route
          path="/research/competitors"
          element={withRouteSuspense(<ResearchCompetitorsPage />)}
        />
        <Route
          path="/research/local"
          element={withRouteSuspense(<ResearchLocalPage />)}
        />
        {/* Legacy research redirects */}
        <Route
          path="/research/portfolio"
          element={<Navigate to="/research/keywords" replace />}
        />
        <Route
          path="/research/history"
          element={<Navigate to="/research/keywords" replace />}
        />
        <Route
          path="/research/competition"
          element={<Navigate to="/research/competitors" replace />}
        />

        {/* Tracking */}
        <Route
          path="/tracking"
          element={<Navigate to="/tracking/keywords" replace />}
        />
        <Route
          path="/tracking/keywords"
          element={withRouteSuspense(<TrackingKeywordsPage />)}
        />
        <Route
          path="/tracking/apps"
          element={withRouteSuspense(<TrackingAppsPage />)}
        />
        <Route
          path="/tracking/competitors"
          element={withRouteSuspense(<TrackingCompetitorsPage />)}
        />
        <Route
          path="/tracking/local"
          element={withRouteSuspense(<TrackingLocalPage />)}
        />
        <Route
          path="/tracking/genai"
          element={withRouteSuspense(<TrackingGenaiPage />)}
        />
        {/* Legacy tracking redirects */}
        <Route
          path="/tracking/seo"
          element={<Navigate to="/tracking/keywords" replace />}
        />
        <Route
          path="/tracking/aso"
          element={<Navigate to="/tracking/apps" replace />}
        />

        {/* Insights */}
        <Route
          path="/insights"
          element={<Navigate to="/insights/alerts" replace />}
        />
        <Route
          path="/insights/alerts"
          element={withRouteSuspense(<InsightsAlertsPage />)}
        />
        <Route
          path="/insights/opportunities"
          element={withRouteSuspense(<InsightsOpportunitiesPage />)}
        />
        <Route
          path="/insights/anomalies"
          element={withRouteSuspense(<InsightsAnomaliesPage />)}
        />
        <Route
          path="/insights/recommendations"
          element={withRouteSuspense(<InsightsRecommendationsPage />)}
        />

        {/* Reports */}
        <Route
          path="/reports"
          element={withRouteSuspense(<ReportsListPage />)}
        />
        <Route
          path="/reports/:reportId"
          element={withRouteSuspense(<ReportDetailPage />)}
        />

        {/* Assets */}
        <Route path="/assets" element={withRouteSuspense(<AssetsListPage />)} />
        <Route
          path="/assets/:assetId"
          element={withRouteSuspense(<AssetDetailPage />)}
        />

        {/* Chat */}
        <Route path="/chat" element={withRouteSuspense(<ChatPage />)} />
        <Route
          path="/chat/:threadId"
          element={withRouteSuspense(<ChatThreadPage />)}
        />
        {/* Legacy AI redirects */}
        <Route path="/ai" element={<Navigate to="/chat" replace />} />
        <Route path="/ai/:threadId" element={<Navigate to="/chat" replace />} />

        {/* Settings */}
        <Route
          path="/settings"
          element={<Navigate to="/settings/workspace" replace />}
        />
        <Route
          path="/settings/workspace"
          element={withRouteSuspense(<SettingsWorkspacePage />)}
        />
        <Route
          path="/settings/account"
          element={withRouteSuspense(<SettingsAccountPage />)}
        />
        <Route
          path="/settings/integrations"
          element={withRouteSuspense(<SettingsIntegrationsPage />)}
        />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
